/**
 * Data-kind classifier — the single source of truth for what a key
 * (port name, parameter name, raw tag name) actually emits.
 *
 * Used by:
 *   - EntityAliasPicker (filter keys by widget compatibility)
 *   - Widget render-time guard (block invalid bindings)
 *   - Copilot proposals (suggest the right widget for a key)
 *
 * Phase 0 reads the `dataKind` field that B0.31 added to PortDef +
 * ParameterDef in foundry-plant.ts. Phase 1 will read from the YAML
 * via the platform's `/v1/twin/entities/:id` response — same field
 * name, same enum.
 */

import type { DataKind } from "@foundry/foundry-plant";
import type { PlantNode } from "@foundry/foundry-plant";
import { readSlot, type DataSource, type WidgetSlot } from "./types";
import type { InvalidBindingProblem } from "./InvalidBindingCard";

export type { DataKind };

/**
 * Human-readable label for each kind — used in tooltips + slot chips.
 */
export const DATA_KIND_LABEL: Record<DataKind, string> = {
  analog_scalar: "Analog scalar",
  analog_percentage: "Percentage",
  count_scalar: "Count",
  derived_kpi: "Derived KPI",
  categorical_enum: "Category",
  categorical_distribution: "Distribution",
  event_stream: "Event stream",
  boolean_state: "On/off state",
  string_label: "Label",
  image: "Image",
};

/**
 * Look up the data-kind for a key on a given alias. Walks the plant to
 * find the source PortDef / ParameterDef. Returns the declared
 * `dataKind` if present, otherwise infers from name+unit heuristics so
 * un-classified keys still get a reasonable bucket.
 */
export function classifyKey(
  nodes: PlantNode[],
  alias: string,
  key: string,
): DataKind {
  // Match by instance name (exact) or by "All <template>s" rollup.
  const matchedNodes = nodes.filter(
    (n) =>
      n.instanceName === alias ||
      alias === `All ${humaniseTemplate(n.entity.templateId)}s`,
  );

  for (const n of matchedNodes) {
    const inPort = n.entity.inputPorts.find((p) => p.name === key);
    if (inPort?.dataKind) return inPort.dataKind;
    const outPort = n.entity.outputPorts.find((p) => p.name === key);
    if (outPort?.dataKind) return outPort.dataKind;
    const param = n.entity.parameters.find((p) => p.name === key);
    if (param?.dataKind) return param.dataKind;
  }

  return inferFromName(key);
}

/**
 * Fallback inference when an explicit dataKind isn't declared. Covers
 * raw-tag bindings (Tier-1 dashboards) and any un-classified parameter.
 */
export function inferFromName(key: string): DataKind {
  const lc = key.toLowerCase();

  // Counts: ends in _count, _qty, _hits, etc.
  if (/_count$|_qty$|_hits$|_total$|_n$/.test(lc)) return "count_scalar";

  // Identifiers / labels
  if (/^id$|_id$|_number$|^name$|_uuid$/.test(lc)) return "string_label";

  // Shift / grade / status enums
  if (/^shift$|^grade$|_status$|_state$|pass_fail/.test(lc)) {
    return "categorical_enum";
  }

  // Boolean flags
  if (/^is_|^has_|_online$|_enabled$|_active$/.test(lc)) return "boolean_state";

  // Percentages / ratios
  if (/_pct$|_percent$|_ratio$|moisture|humidity|rejection_rate/.test(lc)) {
    return "analog_percentage";
  }

  // Composite KPIs
  if (/^oee$|carbon_equivalent|^kpi_/.test(lc)) return "derived_kpi";

  // Default — most numeric process tags are analog scalars
  return "analog_scalar";
}

/**
 * The name rule for a raw tag the org does not have (a demo key, a tag since deleted). Narrower than
 * {@link inferFromName}: the dashboard renderer and the binding picker have always judged raw tags by these rules.
 */
export function inferFromTagName(key: string): DataKind {
  const lc = key.toLowerCase();
  if (/_count$|_qty$/.test(lc)) return "count_scalar";
  if (/^id$|_id$|_number$/.test(lc)) return "string_label";
  if (/^shift$|^grade$|_status$/.test(lc)) return "categorical_enum";
  if (/^is_|_online$|_active$/.test(lc)) return "boolean_state";
  if (/_pct$|_percent$|moisture/.test(lc)) return "analog_percentage";
  if (/^oee$|carbon_equivalent/.test(lc)) return "derived_kpi";
  return "analog_scalar";
}

/** The org's tags' stored data_kind by lower-cased name (the key a tag binding holds). Pure. */
export function storedKindsByName(
  tags: ReadonlyArray<{ name?: string | null; data_kind?: string | null }>,
): Map<string, string> {
  const m = new Map<string, string>();
  for (const t of tags) if (t.name && t.data_kind) m.set(t.name.toLowerCase(), t.data_kind);
  return m;
}

/** The org's tags' stored data_kind by lower-cased tag id (the key a machine's port is judged by, B6). Pure. */
export function storedKindsByTagId(
  tags: ReadonlyArray<{ id?: string | null; data_kind?: string | null }>,
): Map<string, string> {
  const m = new Map<string, string>();
  for (const t of tags) if (t.id && t.data_kind) m.set(t.id.toLowerCase(), t.data_kind);
  return m;
}

/**
 * The stored kinds a board checks tag bindings against, from the org-tags query's state: its loaded tags whenever it
 * holds them, even after a later refetch failed (the query then reports an error and keeps its data); undefined while
 * the first load runs (nothing judged yet); none, so the name rule, when the first load failed or in stub mode. Pure.
 */
export function storedTagKindsOf(q: {
  live: boolean;
  items: ReadonlyArray<{ name?: string | null; data_kind?: string | null }> | undefined;
  isError: boolean;
}): Map<string, string> | undefined {
  if (!q.live) return new Map();
  if (q.items) return storedKindsByName(q.items);
  return q.isError ? new Map() : undefined;
}

/** The same query's tags by id, for the ports a machine's roles resolve to a tag id: as storedTagKindsOf, by tag id. Pure. */
export function storedTagKindsByIdOf(q: {
  live: boolean;
  items: ReadonlyArray<{ id?: string | null; data_kind?: string | null }> | undefined;
  isError: boolean;
}): Map<string, string> | undefined {
  if (!q.live) return new Map();
  if (q.items) return storedKindsByTagId(q.items);
  return q.isError ? new Map() : undefined;
}

function isDataKind(s: string): s is DataKind {
  return Object.prototype.hasOwnProperty.call(DATA_KIND_LABEL, s);
}

/**
 * The kind a tag counts as, from the data_kind the org stores for it (undefined: none, or one that is no kind) and the
 * name the tag has: the stored kind, but never stricter than the name rule the boards were built against (see
 * bindingKind). Pure.
 */
function kindOfTag(name: string, raw: string | undefined, accepts?: readonly DataKind[]): DataKind {
  const named = inferFromTagName(name);
  const stored = raw && isDataKind(raw) ? raw : undefined;
  if (!stored) return named;
  if (accepts && !accepts.includes(stored) && accepts.includes(named)) return named;
  return stored;
}

/**
 * The kind a bound key counts as, for a widget's slot check and the binding picker (2026-10-17). A tag binding reads
 * its tag's own stored data_kind first, since that is what the tag sends: a VisiPour "state.auto_pour_mode" is
 * boolean_state, which no name rule can tell. Given the slot's `accepts`, it is never stricter than the name rule the
 * boards were built against: when the slot takes the name's kind but not the stored one it counts as the name's (66
 * VSync overview boards on prod bind an enum Mode tag to a time series, which their name rule let through).
 * `storedTagKinds` is undefined while the org's tags are loading, and a tag binding is then not judged yet (null), so a
 * valid board never flashes an invalid binding. A tag the org does not have reads by its name. A port binding reads the
 * plant model's declared kind, then its name (classifyKey).
 */
export function bindingKind(
  source: { kind: string; alias: string },
  key: string,
  nodes: PlantNode[],
  storedTagKinds: ReadonlyMap<string, string> | undefined,
  accepts?: readonly DataKind[],
): DataKind | null {
  if (source.kind !== "tag") return classifyKey(nodes, source.alias, key);
  if (!storedTagKinds) return null;
  return kindOfTag(key, storedTagKinds.get(key.toLowerCase()), accepts);
}

/** The tag a machine's port reads, as the binding check judges it: its id (the key it is judged by) and its name. */
export interface PortTagRef {
  id: string;
  name: string;
}

/** Each bound key a widget's slots do not take, by {@link bindingKind}; none for a key not judged yet. Pure. */
export function bindingProblems(
  slots: readonly WidgetSlot[],
  source: DataSource | undefined,
  nodes: PlantNode[],
  storedTagKinds: ReadonlyMap<string, string> | undefined,
  portTag?: (key: string) => PortTagRef | null | undefined,
  storedKindsById?: ReadonlyMap<string, string>,
): InvalidBindingProblem[] {
  const problems: InvalidBindingProblem[] = [];
  if (!source) return problems;
  for (const slot of slots) {
    for (const key of readSlot(source, slot.id)) {
      // A port bound by machine (B6) is judged by what its own tag stores, as a tag binding is, and the tag is found by
      // its id: where two tags share a name, a port's tag is never judged by the other's kind. A port that reads no tag
      // (two feeds, not bound) is not judged: the widget says why it draws nothing. While the org's tags load (no kinds
      // given yet) nothing is judged; given only the names' kinds, the name finds the tag as it did.
      const tag = source.kind === "port" ? portTag?.(key) : undefined;
      if (tag === null) continue;
      let kind: DataKind | null;
      if (tag === undefined) kind = bindingKind(source, key, nodes, storedTagKinds, slot.dataKinds);
      else if (storedKindsById) kind = kindOfTag(tag.name, storedKindsById.get(tag.id.toLowerCase()), slot.dataKinds);
      else kind = bindingKind({ kind: "tag", alias: source.alias }, tag.name, nodes, storedTagKinds, slot.dataKinds);
      if (kind && !slot.dataKinds.includes(kind)) {
        problems.push({ slotLabel: slot.label, key, actualKind: kind, allowedKinds: slot.dataKinds });
      }
    }
  }
  return problems;
}

/**
 * Mirror of EntityAliasPicker's `humaniseTemplate` so classifyKey() can
 * resolve "All induction furnaces"-style rollups without circular imports.
 */
function humaniseTemplate(t: string): string {
  return (t.split(".").pop() ?? t)
    .replace(/_/g, " ")
    .replace(/\b\w/g, (m) => m.toUpperCase());
}

/**
 * Build a compatibility tooltip explaining why a key can't be bound to
 * a particular slot.
 */
export function bindingError(
  widgetName: string,
  slotLabel: string,
  allowed: DataKind[],
  actual: DataKind,
  key: string,
): string {
  const allowedLabels = allowed.map((k) => DATA_KIND_LABEL[k]).join(" / ");
  return `${widgetName} ${slotLabel} needs ${allowedLabels}. \`${key}\` is ${DATA_KIND_LABEL[actual]}.`;
}
